import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/GlobalProvider";
import { CRUD_API_EDITPROFILE } from "../../hooks/APIHooks";
import { Link, useNavigate } from "react-router-dom";
import { FaSpinner, FaMapMarkerAlt, FaHome, FaBuilding, FaExclamationTriangle } from "react-icons/fa";

const ProfileEdit = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userData, setUserData] = useState({
    user_id: "",
    emailaddress: "",
    phonenumber: "",
    sex: "",
    password: "", // Only for password updates
  });
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [newAddress, setNewAddress] = useState({
    address_type: "home",
    full_name: "",
    phone_number: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    is_default: false
  });
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState("");

  useEffect(() => {
    if (user && user.name) {
      const nameParts = user.name.split(" ");
      setFirstName(nameParts[0] || "");
      setLastName(nameParts.slice(1).join(" ") || "");
      setUserData((prevUserData) => ({
        ...prevUserData,
        user_id: user.user_id,
        emailaddress: user.emailaddress,
        phonenumber: user.phonenumber || "",
        sex: user.sex || "",
      }));

      // Set default address form values
      setNewAddress(prev => ({
        ...prev,
        full_name: user.name,
        phone_number: user.phonenumber || ""
      }));

      // Fetch user addresses
      fetchUserAddresses();
    }
  }, [user]);

  const fetchUserAddresses = async () => {
    try {
      setAddressLoading(true);
      setAddressError("");
      
      console.log("Fetching addresses for user:", user.user_id);
      
      const response = await fetch(`${CRUD_API_EDITPROFILE}?user_id=${user.user_id}&action=get_addresses`);
      
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Address API response:", data);
      
      if (data.status === "success") {
        setAddresses(data.addresses || []);
        // Select the default address if exists
        const defaultAddress = data.addresses.find(addr => addr.is_default);
        if (defaultAddress) {
          setSelectedAddress(defaultAddress);
        } else if (data.addresses.length > 0) {
          setSelectedAddress(data.addresses[0]);
        }
      } else {
        setAddressError(data.message || "Failed to load addresses");
      }
    } catch (err) {
      console.error("Error fetching addresses:", err);
      setAddressError("Failed to load addresses. Please try again.");
    } finally {
      setAddressLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
  };

  const handleAddressInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAddress(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
    setShowAddressForm(false);
  };

  const validateForm = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError("First name and last name are required");
      return false;
    }

    if (!userData.emailaddress.trim()) {
      setError("Email address is required");
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(userData.emailaddress)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!userData.phonenumber.trim()) {
      setError("Phone number is required");
      return false;
    }

    if (!/^[0-9]{10}$/.test(userData.phonenumber)) {
      setError("Phone number must be 10 digits");
      return false;
    }

    if (userData.password && userData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }

    return true;
  };

  const validateAddress = (address) => {
    if (!address.full_name.trim()) {
      setError("Full name is required for address");
      return false;
    }

    if (!address.phone_number.trim() || !/^[0-9]{10}$/.test(address.phone_number)) {
      setError("Valid phone number is required for address");
      return false;
    }

    if (!address.address_line1.trim()) {
      setError("Address line 1 is required");
      return false;
    }

    if (!address.city.trim()) {
      setError("City is required");
      return false;
    }

    if (!address.state.trim()) {
      setError("State is required");
      return false;
    }

    if (!address.pincode.trim() || !/^[0-9]{6}$/.test(address.pincode)) {
      setError("Valid 6-digit pincode is required");
      return false;
    }

    return true;
  };

  const saveAddress = async () => {
    if (!validateAddress(newAddress)) return;

    try {
      setAddressLoading(true);
      setError("");
      
      const response = await fetch(CRUD_API_EDITPROFILE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "save_address",
          user_id: userData.user_id,
          address: newAddress
        }),
      });

      const data = await response.json();

      if (data.status === "success") {
        setSuccessMessage("Address saved successfully!");
        setShowAddressForm(false);
        setNewAddress({
          address_type: "home",
          full_name: `${firstName} ${lastName}`,
          phone_number: userData.phonenumber,
          address_line1: "",
          address_line2: "",
          city: "",
          state: "",
          pincode: "",
          country: "India",
          is_default: false
        });
        fetchUserAddresses(); // Refresh addresses list
      } else {
        setError(data.message || "Failed to save address");
      }
    } catch (err) {
      console.error("Address save error:", err);
      setError("Network error while saving address");
    } finally {
      setAddressLoading(false);
    }
  };

  const onUpdatePress = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const updateData = {
        user_id: userData.user_id,
        name: `${firstName} ${lastName}`,
        emailaddress: userData.emailaddress,
        phonenumber: userData.phonenumber,
        sex: userData.sex,
      };

      // Only include password if it's provided (user wants to change it)
      if (userData.password.trim()) {
        updateData.password = userData.password;
      }

      const response = await fetch(CRUD_API_EDITPROFILE, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.status === "success") {
          setSuccessMessage("Profile updated successfully! Redirecting to login...");
          setTimeout(() => {
            signOut();
            navigate("/signin");
          }, 2000);
        } else {
          setError(data.message || "Failed to update profile");
        }
      } else {
        setError(data.message || "Failed to update profile. Please try again.");
      }
    } catch (err) {
      console.error("Update error", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const getAddressIcon = (type) => {
    switch (type) {
      case 'home': return <FaHome className="text-green-500" />;
      case 'office': return <FaBuilding className="text-blue-500" />;
      default: return <FaMapMarkerAlt className="text-gray-500" />;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-white to-gray-100 p-4 pt-20">
      <div className="w-full max-w-2xl bg-white p-6 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-center mb-2">Edit Your Profile</h1>
        <p className="text-sm text-center text-gray-600 mb-6">
          You will be logged out after updating your profile. Please sign in again.
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Personal Information</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  value={firstName}
                  placeholder="First Name *"
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  disabled={loading}
                />
              </div>

              <div>
                <input
                  type="text"
                  value={lastName}
                  placeholder="Last Name *"
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <input
                type="email"
                name="emailaddress"
                value={userData.emailaddress}
                placeholder="Email Address *"
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                disabled={loading}
              />
            </div>

            <div>
              <input
                type="tel"
                name="phonenumber"
                value={userData.phonenumber}
                placeholder="Phone Number * (10 digits)"
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                disabled={loading}
              />
            </div>

            <div>
              <select
                name="sex"
                value={userData.sex}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                disabled={loading}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <input
                type="password"
                name="password"
                value={userData.password}
                placeholder="New Password (leave blank to keep current)"
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Only enter if you want to change your password
              </p>
            </div>
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <h2 className="text-lg font-semibold">Address Information</h2>
              <button
                onClick={() => setShowAddressForm(!showAddressForm)}
                className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                disabled={addressLoading}
              >
                {showAddressForm ? 'Cancel' : '+ Add Address'}
              </button>
            </div>

            {showAddressForm ? (
              <div className="space-y-3 p-4 border border-blue-200 rounded-lg bg-blue-50">
                <h3 className="font-medium">Add New Address</h3>
                
                <select
                  name="address_type"
                  value={newAddress.address_type}
                  onChange={handleAddressInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                >
                  <option value="home">Home</option>
                  <option value="office">Office</option>
                  <option value="other">Other</option>
                </select>

                <input
                  type="text"
                  name="full_name"
                  value={newAddress.full_name}
                  placeholder="Full Name *"
                  onChange={handleAddressInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />

                <input
                  type="tel"
                  name="phone_number"
                  value={newAddress.phone_number}
                  placeholder="Phone Number *"
                  onChange={handleAddressInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />

                <input
                  type="text"
                  name="address_line1"
                  value={newAddress.address_line1}
                  placeholder="Address Line 1 *"
                  onChange={handleAddressInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />

                <input
                  type="text"
                  name="address_line2"
                  value={newAddress.address_line2}
                  placeholder="Address Line 2 (Optional)"
                  onChange={handleAddressInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />

                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    name="city"
                    value={newAddress.city}
                    placeholder="City *"
                    onChange={handleAddressInputChange}
                    className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />

                  <input
                    type="text"
                    name="state"
                    value={newAddress.state}
                    placeholder="State *"
                    onChange={handleAddressInputChange}
                    className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    name="pincode"
                    value={newAddress.pincode}
                    placeholder="Pincode *"
                    onChange={handleAddressInputChange}
                    className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />

                  <input
                    type="text"
                    name="country"
                    value={newAddress.country}
                    placeholder="Country"
                    onChange={handleAddressInputChange}
                    className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="is_default"
                    checked={newAddress.is_default}
                    onChange={handleAddressInputChange}
                    className="rounded"
                  />
                  <span className="text-sm">Set as default address</span>
                </label>

                <button
                  onClick={saveAddress}
                  disabled={addressLoading}
                  className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {addressLoading ? <FaSpinner className="animate-spin" /> : null}
                  <span>Save Address</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {addressLoading ? (
                  <div className="text-center py-4">
                    <FaSpinner className="animate-spin mx-auto text-blue-500" />
                    <p className="text-sm text-gray-500 mt-2">Loading addresses...</p>
                  </div>
                ) : addressError ? (
                  <div className="text-center py-4 text-red-500">
                    <FaExclamationTriangle className="mx-auto text-2xl mb-2" />
                    <p className="text-sm">{addressError}</p>
                    <button
                      onClick={fetchUserAddresses}
                      className="mt-2 text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      Retry
                    </button>
                  </div>
                ) : addresses.length > 0 ? (
                  addresses.map((address) => (
                    <div
                      key={address.address_id}
                      onClick={() => handleAddressSelect(address)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedAddress?.address_id === address.address_id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        {getAddressIcon(address.address_type)}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <span className="font-medium capitalize">{address.address_type}</span>
                            {address.is_default && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Default</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{address.full_name}</p>
                          <p className="text-sm text-gray-600">{address.phone_number}</p>
                          <p className="text-sm text-gray-600">
                            {address.address_line1}, {address.address_line2 && `${address.address_line2}, `}
                            {address.city}, {address.state} - {address.pincode}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <FaMapMarkerAlt className="mx-auto text-2xl mb-2" />
                    <p>No addresses saved yet</p>
                    <p className="text-sm mt-1">Click "Add Address" to create one</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600 text-center">{successMessage}</p>
          </div>
        )}

        {/* Buttons */}
        <div className="mt-6 space-y-3">
          <button
            onClick={onUpdatePress}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" />
                <span>Updating Profile...</span>
              </>
            ) : (
              <span>Update Profile</span>
            )}
          </button>
          
          <Link to="/user/dashboard">
            <button
              disabled={loading}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </Link>
        </div>
      </div>

      <p className="text-xs font-light text-gray-500 mt-6 text-center pb-5">
        www.thewinnerindia.com <br />
        &copy; {new Date().getFullYear()} thewinnerindia. All rights reserved.
      </p>
    </div>
  );
};

export default ProfileEdit;